import { useState, useCallback, useMemo } from 'react';
import { router, Head, Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSidebarState } from '@/components/CollapsibleSidebar';

/**
 * Tome page data from the backend.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    content: string | null;
    order: number;
}

/**
 * Props for the Tome Index page.
 */
interface Props {
    pages: TomePage[];
    currentPage: TomePage | null;
    searchQuery: string | null;
}

/**
 * Public Tome viewer page - displays the Archivist's Tome with a book-like UI.
 * Features a table of contents sidebar, search functionality, and markdown rendering.
 */
export default function Index({ pages, currentPage, searchQuery: initialSearchQuery }: Props) {
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    // Use responsive sidebar state - hidden by default on mobile, visible on desktop
    const { isOpen: sidebarOpen, isMobile, toggle: toggleSidebar, close: closeSidebar } = useSidebarState(true, false);

    /**
     * Handle search submission.
     */
    const handleSearch = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            router.get('/tome/search', { q: searchQuery.trim() }, { preserveState: true });
        }
    }, [searchQuery]);

    /**
     * Clear search and return to table of contents.
     */
    const handleClearSearch = useCallback(() => {
        setSearchQuery('');
        router.get('/tome', {}, { preserveState: true });
    }, []);

    /**
     * Filter pages based on search query for highlighting in TOC.
     */
    const filteredPages = useMemo(() => {
        if (!initialSearchQuery) return pages;
        const query = initialSearchQuery.toLowerCase();
        return pages.filter(page =>
            page.name.toLowerCase().includes(query) ||
            (page.content && page.content.toLowerCase().includes(query))
        );
    }, [pages, initialSearchQuery]);

    return (
        <>
            <Head title="Archivist's Tome" />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex relative">
                {/* Mobile overlay backdrop - shown when sidebar is open on mobile */}
                {isMobile && sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 md:hidden"
                        onClick={closeSidebar}
                        aria-hidden="true"
                    />
                )}

                {/* Sidebar - Table of Contents */}
                <aside
                    className={`
                        ${sidebarOpen ? 'w-72' : 'w-0'}
                        transition-all duration-300
                        bg-slate-800/80 border-r border-amber-900/30
                        overflow-hidden flex-shrink-0
                        ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}
                    `}
                >
                    <div className="w-72 h-full flex flex-col">
                        {/* Tome Header */}
                        <div className="p-4 border-b border-amber-900/30 bg-gradient-to-r from-amber-900/20 to-transparent">
                            <h1 className="text-xl font-serif text-amber-200 flex items-center gap-2">
                                📖 Archivist's Tome
                            </h1>
                            <p className="text-xs text-amber-200/60 mt-1">
                                A repository of lore and knowledge
                            </p>
                        </div>

                        {/* Search Bar */}
                        <div className="p-3 border-b border-amber-900/30">
                            <form onSubmit={handleSearch} className="flex gap-2">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search the tome..."
                                    className="flex-1 px-3 py-1.5 bg-slate-700/50 border border-amber-900/30 rounded text-amber-100 placeholder-amber-200/40 text-sm focus:outline-none focus:border-amber-600"
                                />
                                <button
                                    type="submit"
                                    className="px-3 py-1.5 bg-amber-800/50 hover:bg-amber-700/50 text-amber-200 rounded text-sm transition-colors"
                                >
                                    🔍
                                </button>
                            </form>
                            {initialSearchQuery && (
                                <button
                                    onClick={handleClearSearch}
                                    className="mt-2 text-xs text-amber-400 hover:text-amber-300"
                                >
                                    ← Clear search
                                </button>
                            )}
                        </div>

                        {/* Table of Contents */}
                        <nav className="flex-1 overflow-y-auto p-3">
                            <h2 className="text-xs font-semibold text-amber-200/60 uppercase tracking-wider mb-2">
                                {initialSearchQuery ? `Search Results (${filteredPages.length})` : 'Table of Contents'}
                            </h2>
                            {filteredPages.length === 0 ? (
                                <p className="text-sm text-amber-200/40 italic">
                                    {initialSearchQuery ? 'No pages match your search.' : 'No pages in the tome yet.'}
                                </p>
                            ) : (
                                <ul className="space-y-1">
                                    {filteredPages.map((page) => (
                                        <li key={page.id}>
                                            <Link
                                                href={`/tome/page/${page.slug}`}
                                                onClick={() => isMobile && closeSidebar()}
                                                className={`block px-3 py-2 rounded text-sm transition-colors ${
                                                    currentPage?.id === page.id
                                                        ? 'bg-amber-800/40 text-amber-100 border-l-2 border-amber-500'
                                                        : 'text-amber-200/80 hover:bg-amber-800/20 hover:text-amber-100'
                                                }`}
                                            >
                                                {page.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </nav>

                        {/* Back to Main Site */}
                        <div className="p-3 border-t border-amber-900/30">
                            <Link
                                href="/"
                                className="block text-center text-sm text-amber-200/60 hover:text-amber-200 transition-colors"
                            >
                                ← Return to the realm
                            </Link>
                        </div>
                    </div>
                </aside>

                {/* Toggle Sidebar Button - hamburger icon with state indicator */}
                <button
                    onClick={toggleSidebar}
                    className={`
                        fixed top-4 z-50
                        p-2.5 bg-slate-800/90 border border-amber-900/30 rounded-lg
                        text-amber-200 hover:bg-amber-800/30
                        transition-all duration-300
                        focus:outline-none focus:ring-2 focus:ring-amber-500
                        ${isMobile ? 'left-4' : ''}
                    `}
                    style={!isMobile ? { left: sidebarOpen ? 'calc(18rem + 0.5rem)' : '0.5rem' } : undefined}
                    aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                    aria-expanded={sidebarOpen}
                >
                    {/* Hamburger / X icon */}
                    <span className="block w-5 h-5 relative">
                        <span
                            className={`
                                absolute left-0 w-5 h-0.5 bg-current
                                transition-all duration-300
                                ${sidebarOpen ? 'top-2 rotate-45' : 'top-1'}
                            `}
                        />
                        <span
                            className={`
                                absolute left-0 top-2 w-5 h-0.5 bg-current
                                transition-all duration-300
                                ${sidebarOpen ? 'opacity-0' : 'opacity-100'}
                            `}
                        />
                        <span
                            className={`
                                absolute left-0 w-5 h-0.5 bg-current
                                transition-all duration-300
                                ${sidebarOpen ? 'top-2 -rotate-45' : 'top-3'}
                            `}
                        />
                    </span>
                </button>

                {/* Main Content Area - Page Display */}
                <main className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto p-8 pt-16 md:pt-8">
                        {currentPage ? (
                            <TomePageContent page={currentPage} />
                        ) : (
                            <TomeWelcome pageCount={pages.length} />
                        )}
                    </div>
                </main>
            </div>
        </>
    );
}

/**
 * TomeWelcome - Displayed when no specific page is selected.
 * Shows a welcome message and overview of the tome.
 */
function TomeWelcome({ pageCount }: { pageCount: number }) {
    return (
        <div className="text-center py-16">
            <div className="text-6xl mb-6">📖</div>
            <h1 className="text-3xl font-serif text-amber-200 mb-4">
                Welcome to the Archivist's Tome
            </h1>
            <p className="text-amber-200/70 max-w-lg mx-auto mb-8">
                Within these pages lies the collected knowledge of our realm.
                Select a chapter from the table of contents to begin your journey,
                or use the search to find specific lore.
            </p>
            {pageCount > 0 ? (
                <p className="text-amber-200/50 text-sm">
                    {pageCount} page{pageCount !== 1 ? 's' : ''} of wisdom await you.
                </p>
            ) : (
                <p className="text-amber-200/50 text-sm italic">
                    The tome awaits its first entries...
                </p>
            )}
        </div>
    );
}

/**
 * TomePageContent - Displays a single tome page with markdown rendering.
 * Styled to look like an old book page.
 */
function TomePageContent({ page }: { page: TomePage }) {
    return (
        <article className="bg-gradient-to-br from-amber-50/5 to-amber-100/5 border border-amber-900/30 rounded-lg shadow-2xl">
            {/* Page Header */}
            <header className="p-6 border-b border-amber-900/20 bg-gradient-to-r from-amber-900/10 to-transparent">
                <h1 className="text-2xl font-serif text-amber-100">
                    {page.name}
                </h1>
            </header>

            {/* Page Content - Markdown */}
            <div className="p-6 prose prose-invert prose-amber max-w-none">
                {page.content ? (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            // Custom styling for markdown elements to match the tome theme
                            h1: ({ children }) => <h1 className="text-2xl font-serif text-amber-200 border-b border-amber-900/30 pb-2 mb-4">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-xl font-serif text-amber-200 mt-6 mb-3">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-lg font-serif text-amber-200 mt-4 mb-2">{children}</h3>,
                            p: ({ children }) => <p className="text-amber-100/90 leading-relaxed mb-4">{children}</p>,
                            a: ({ href, children }) => <a href={href} className="text-amber-400 hover:text-amber-300 underline">{children}</a>,
                            ul: ({ children }) => <ul className="list-disc list-inside text-amber-100/90 mb-4 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside text-amber-100/90 mb-4 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-amber-100/90">{children}</li>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-amber-600 pl-4 italic text-amber-200/80 my-4">{children}</blockquote>,
                            code: ({ children }) => <code className="bg-slate-800 px-1.5 py-0.5 rounded text-amber-300 text-sm">{children}</code>,
                            pre: ({ children }) => <pre className="bg-slate-800 p-4 rounded overflow-x-auto my-4">{children}</pre>,
                            img: ({ src, alt }) => (
                                <img
                                    src={src}
                                    alt={alt || ''}
                                    className="max-w-full h-auto rounded border border-amber-900/30 my-4"
                                />
                            ),
                            hr: () => <hr className="border-amber-900/30 my-6" />,
                            table: ({ children }) => <table className="w-full border-collapse my-4">{children}</table>,
                            th: ({ children }) => <th className="border border-amber-900/30 bg-amber-900/20 px-3 py-2 text-left text-amber-200">{children}</th>,
                            td: ({ children }) => <td className="border border-amber-900/30 px-3 py-2 text-amber-100/90">{children}</td>,
                        }}
                    >
                        {page.content}
                    </ReactMarkdown>
                ) : (
                    <p className="text-amber-200/50 italic">
                        This page is empty. The scribe has yet to fill these pages...
                    </p>
                )}
            </div>
        </article>
    );
}

